import { Link, LinkOff } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlPermissions } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import { useMapEditorFeatureHover } from "~/modules/mapEditor/hooks/useMapEditorFeatureHover";

import { ObservationMediaDisplay } from "../ObservationMediaDisplay";
import { ObservationCardFragment } from "./fragments.generated";
import ObservationCardAnswers from "./ObservationCardAnswers";

type BaseObservationCardProps = {
    disabled?: boolean;
    observation: ObservationCardFragment;
};

type LinkableObservationCardProps =
    | {
          isLinked: true;
          onUnlinkClick: (id: string) => Promise<void>;
          onLinkClick?: never;
      }
    | {
          isLinked: false;
          onLinkClick: (id: string) => Promise<void>;
          onUnlinkClick?: never;
      };

type ObservationCardProps = BaseObservationCardProps & Partial<LinkableObservationCardProps>;

export function ObservationCard({ disabled = false, observation, isLinked, onUnlinkClick, onLinkClick }: ObservationCardProps) {
    const { t } = useTranslation("mapEditor");
    const hovering = useMapEditorFeatureHover();
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    return (
        <Stack
            onMouseEnter={() => hovering.set(observation.featureId)}
            onMouseLeave={() => hovering.clear()}
            rowGap={1.5}
            sx={theme => ({
                padding: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                width: "100%",
            })}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" columnGap={2}>
                {/* Note: Labels are shown on hover to keep the card compact */}
                <Stack>
                    <Typography variant="caption" color="textSecondary">
                        {formatDateTime(parseStringToDate(observation.createdAt))}
                    </Typography>
                    <Typography fontWeight={500} title={t("observationCard.labelSpecies")}>
                        {observation.species.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" title={t("observationCard.labelObservationType")}>
                        {observation.type.name}
                    </Typography>
                </Stack>
                {(onUnlinkClick !== undefined || onLinkClick !== undefined) && (
                    <Box>
                        {isLinked ? (
                            <Button
                                startIcon={<LinkOff />}
                                variant="outlined"
                                color="error"
                                disabled={disabled}
                                onClick={e => {
                                    e.preventDefault();
                                    onUnlinkClick?.(observation.id);
                                }}
                            >
                                {t("observationCard.btnUnlink")}
                            </Button>
                        ) : (
                            <Button
                                startIcon={<Link />}
                                variant="outlined"
                                disabled={disabled}
                                onClick={e => {
                                    e.preventDefault();
                                    onLinkClick?.(observation.id);
                                }}
                            >
                                {t("observationCard.btnLink")}
                            </Button>
                        )}
                    </Box>
                )}
            </Stack>
            {isAdmin && (
                <ObservationCardAnswers formTemplate={observation.type.formTemplate} answers={observation.answers ?? null} />
            )}
            {observation.medias.length > 0 && (
                <Stack rowGap={1}>
                    <Typography variant="body2" color="textSecondary">
                        {t("observationCard.labelMedia")}
                    </Typography>
                    <ObservationMediaDisplay media={observation.medias} />
                </Stack>
            )}
        </Stack>
    );
}
